import { buttonStyles } from '@amore/ui/components/button'
import { Link } from '@tanstack/react-router'
import {
  CalendarClock,
  Camera,
  ClipboardList,
  Flower2,
  Heart,
  type LucideIcon,
  MapPin,
  Music,
  Sparkles,
  Store,
  Users,
  UtensilsCrossed,
  Wallet,
} from 'lucide-react'
import type { ReactNode } from 'react'

const modules = {
  budget: { label: 'Rozpočet', icon: Wallet, to: '/budget' },
  suppliers: { label: 'Moji dodavatelé', icon: Store, to: '/suppliers' },
  tasks: { label: 'Úkoly', icon: ClipboardList, to: '/tasks' },
  guests: { label: 'Hosté', icon: Users, to: '/guests' },
  schedule: { label: 'Harmonogram', icon: CalendarClock, to: '/schedule' },
  venue: { label: 'Místa', icon: MapPin, to: '/venues' },
  ceremony: { label: 'Obřad', icon: Heart, to: '/ceremony' },
  reception: { label: 'Jídlo a pití', icon: UtensilsCrossed, to: '/reception' },
  photo: { label: 'Foto a video', icon: Camera, to: '/photo' },
  music: { label: 'Hudba a zábava', icon: Music, to: '/soon/hudba-a-zabava' },
  beauty: { label: 'Vizážista a stylistka', icon: Sparkles, to: '/soon/vizazista-a-stylistka' },
  decor: { label: 'Vizuál a dekorace', icon: Flower2, to: '/soon/vizual-a-dekorace' },
} satisfies Record<string, { label: string; icon: LucideIcon; to: string }>

export type LinkedModule = keyof typeof modules

/**
 * A compact row pointing at a record another module owns, e.g. the budget
 * payment behind a task. Icon, module label, title and one meta line on the
 * left; one action on the far right; everything vertically centred.
 */
export function LinkedRecord({
  module,
  title,
  meta,
  status,
  actionLabel,
  to,
  onAction,
}: {
  module: LinkedModule
  title: ReactNode
  /** One line, e.g. "Záloha · 8 000 Kč". */
  meta?: ReactNode
  /** An optional read-only badge, e.g. the supplier's status. */
  status?: ReactNode
  /** Defaults to "Otevřít". */
  actionLabel?: string
  /** Where the action leads, e.g. the record itself (`/budget/e-photo`); defaults to the module. */
  to?: string
  /** Overrides navigating to the module. */
  onAction?: () => void
}) {
  const config = modules[module]
  const Icon = config.icon
  const actionClass = buttonStyles({ appearance: 'outline', tone: 'neutral', size: 'sm' })
  const label = actionLabel ?? 'Otevřít'
  return (
    <div className="flex items-center gap-4">
      <span
        aria-hidden="true"
        className="grid size-10 shrink-0 place-items-center rounded-full bg-panel-accent text-action"
      >
        <Icon className="size-4.5" strokeWidth={1.8} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{title}</p>
        <p className="truncate text-sm text-muted-foreground">{meta ?? config.label}</p>
      </div>
      {status ? <div className="hidden shrink-0 sm:block">{status}</div> : null}
      {onAction ? (
        <button type="button" onClick={onAction} className={actionClass}>
          {label} ↗
        </button>
      ) : (
        <Link to={to ?? config.to} className={actionClass}>
          {label} ↗
        </Link>
      )}
    </div>
  )
}

/** A module's label, icon and route, for showing it outside a `LinkedRecord` (e.g. as an icon in a table). */
export const linkedModuleConfig = (module: LinkedModule) => modules[module]
